import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandlerFn,
	HttpInterceptorFn,
	HttpRequest,
	HttpStatusCode
} from '@angular/common/http'
import { inject } from '@angular/core'

import { TokenResponse, TokenStorage } from '@interfaces'
import { AlertService, AuthService, PaymentContractService, TokenService } from '@services'
import { Observable, catchError, filter, switchMap, take, throwError } from 'rxjs'
import { SUPPRESS_GLOBAL_ERROR_ALERT } from './http-context.tokens'

export const errorInterceptor: HttpInterceptorFn = (
	request: HttpRequest<unknown>,
	next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
	const tokenService = inject(TokenService)
	const authService = inject(AuthService)
	const alertService = inject(AlertService)
	const paymentContractService = inject(PaymentContractService)

	const noErrorRequest = ['/login/token', '/login/refresh-token']

	if (noErrorRequest.some(r => request.url.includes(r))) {
		return next(request).pipe(
			catchError((error: HttpErrorResponse) => {
				authService.logout()
				return throwError(() => error)
			})
		)
	}

	return next(request).pipe(
		catchError((error: HttpErrorResponse) => {
			// Si el error es 403 (Token inválido o expirado)
			if (error.status === HttpStatusCode.Forbidden) {
				return tokenService.isRefreshing$.pipe(
					take(1),
					switchMap(isRefreshing => {
						if (!isRefreshing) {
							tokenService.setRefreshing(true)

							return authService.refreshToken().pipe(
								switchMap((newToken: TokenResponse) => {
									authService.updateSession(newToken)
									tokenService.setRefreshing(false)

									// Reintentar la petición original con el nuevo token
									const clonedRequest = addToken(request, newToken.access_token)
									return next(clonedRequest)
								}),
								catchError(err => {
									tokenService.setRefreshing(false)
									authService.logout()
									return throwError(() => err)
								})
							)
						} else {
							// Si ya hay un refresh en curso espera a que termine
							return tokenService.isRefreshing$.pipe(
								filter(isRefreshing => !isRefreshing), // Espera hasta que isRefreshing sea false
								take(1),
								switchMap(() => {
									// Reintentar la peticion original con el token actualizado
									const tokenStorage: TokenStorage = JSON.parse(localStorage.getItem('token')!)
									const clonedRequest = addToken(request, tokenStorage.access_token)
									return next(clonedRequest)
								})
							)
						}
					})
				)
			} else {
				if (
					request.context.get(SUPPRESS_GLOBAL_ERROR_ALERT) ||
					paymentContractService.shouldBypassGlobalHandling(request, error)
				) {
					return throwError(() => error)
				}

				// Manejo de otros errores
				const errorMessage = resolveGlobalErrorMessage(error)
				alertService.openAlert(errorMessage)
				return throwError(() => error)
			}
		})
	)
}

function resolveGlobalErrorMessage(error: HttpErrorResponse): string {
	const candidates: unknown[] = [error.error?.error, error.error?.message, error.error, error.message]

	for (const candidate of candidates) {
		const normalized = normalizeErrorCandidate(candidate)
		if (normalized) {
			return normalized
		}
	}

	return 'UNEXPECTED_ERROR'
}

function normalizeErrorCandidate(candidate: unknown): string | null {
	if (typeof candidate === 'string') {
		const trimmed = candidate.trim()
		if (trimmed.length > 0 && trimmed !== '[object Object]') {
			return trimmed
		}
		return null
	}

	if (!candidate || typeof candidate !== 'object') {
		return null
	}

	const map = candidate as Record<string, unknown>
	const nestedCandidates: unknown[] = [map.error, map.message, map.errorMessage, map.title]

	for (const nestedCandidate of nestedCandidates) {
		if (typeof nestedCandidate === 'string') {
			const trimmed = nestedCandidate.trim()
			if (trimmed.length > 0 && trimmed !== '[object Object]') {
				return trimmed
			}
		}
	}

	return null
}

// Función para añadir el token a la solicitud
function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
	return request.clone({
		setHeaders: {
			Authorization: `Bearer ${token}`
		}
	})
}
